import React, { useState, useEffect, useRef } from 'react';
import { bool, func, oneOfType, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { ensureUser, ensureCurrentUser } from '../../../util/data';
import { propTypes } from '../../../util/types';

import { AvatarLarge, NamedLink, InlineTextButton, DraftContent } from '../../../components';

import css from './UserCard.module.css';

const UserCard = props => {
  const MAX_HEIGHT_BIO_WITHOUT_BUTTON = 95;
  const VIEWPORT_MEDIUM = 768;

  const {
    rootClassName,
    className,
    user,
    currentUser,
    onContactUser,
    showContact,
    customAuthorSlug,
  } = props;

  const [showFullBio, setShowFullBio] = useState(false);
  const [shouldShowSeeMore, setShouldShowSeeMore] = useState(false);
  const [maxHeight, setMaxHeight] = useState(MAX_HEIGHT_BIO_WITHOUT_BUTTON);
  const desktopBioRef = useRef(null);
  const mobileBioRef = useRef(null);
  let targetRef = null;
  if (typeof window !== 'undefined') {
    targetRef = window.innerWidth > VIEWPORT_MEDIUM ? desktopBioRef : mobileBioRef;
  }

  const userIsCurrentUser = user && user.type === 'currentUser';
  const ensuredUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const isCurrentUser =
    ensuredUser.id && ensuredCurrentUser.id && ensuredUser.id.uuid === ensuredCurrentUser.id.uuid;
  const { displayName, bio } = ensuredUser.attributes.profile;

  useEffect(() => {
    if (targetRef.current) {
      const contentHeight = targetRef.current.scrollHeight;
      setShouldShowSeeMore(contentHeight > MAX_HEIGHT_BIO_WITHOUT_BUTTON);
    }
  }, [bio]);

  const handleContactUserClick = () => {
    onContactUser(user);
  };

  const hasBio = !!bio;
  const classes = classNames(rootClassName || css.root, className);
  const linkClasses = classNames(css.links, {
    [css.withBioMissingAbove]: !hasBio,
  });
  const viewBio = classNames(css.bio, { [css.fullBio]: showFullBio || !shouldShowSeeMore });

  const separator =
    isCurrentUser || !showContact ? null : <span className={css.linkSeparator}>•</span>;

  const toggleBio = () => {
    if (!showFullBio) {
      setMaxHeight(targetRef.current.scrollHeight);
    } else {
      setMaxHeight(MAX_HEIGHT_BIO_WITHOUT_BUTTON);
    }
    setShowFullBio(!showFullBio);
  };

  const contact = showContact ? (
    <InlineTextButton
      rootClassName={css.contact}
      onClick={handleContactUserClick}
      enforcePagePreloadFor="SignupPage"
    >
      <FormattedMessage id="UserCard.contactUser" />
    </InlineTextButton>
  ) : null;

  const editProfileMobile = (
    <span className={css.editProfileMobile}>
      <span className={css.linkSeparator}>•</span>
      <NamedLink name="ProfileSettingsPage">
        <FormattedMessage id="ListingPage.editProfileLink" />
      </NamedLink>
    </span>
  );

  const editProfileDesktop = isCurrentUser ? (
    <NamedLink className={css.editProfileDesktop} name="ProfileSettingsPage">
      <FormattedMessage id="ListingPage.editProfileLink" />
    </NamedLink>
  ) : null;

  const links = ensuredUser.id ? (
    <p className={linkClasses}>
      {customAuthorSlug.length > 0 ? (
        <NamedLink
          className={css.link}
          name="ProfilePageWithCustomSlug"
          params={{ customSlug: customAuthorSlug }}
        >
          <FormattedMessage id="UserCard.viewProfileLink" />
        </NamedLink>
      ) : (
        <NamedLink className={css.link} name="ProfilePage" params={{ id: ensuredUser.id.uuid }}>
          <FormattedMessage id="UserCard.viewProfileLink" />
        </NamedLink>
      )}
      {separator}
      {isCurrentUser ? editProfileMobile : contact}
    </p>
  ) : null;

  return (
    <div className={classes}>
      <div className={css.content}>
        <AvatarLarge className={css.avatar} user={user} customSlug={customAuthorSlug} />
        <div className={css.info}>
          <div className={css.headingRow}>
            <FormattedMessage id="UserCard.heading" values={{ name: displayName }} />
            {editProfileDesktop}
          </div>
          {hasBio ? (
            <div className={css.desktopBio}>
              <div ref={desktopBioRef} className={viewBio} style={{ maxHeight: `${maxHeight}px` }}>
                <DraftContent draftContent={bio} />
              </div>
              {shouldShowSeeMore && (
                <div className={css.btnBox}>
                  <InlineTextButton onClick={toggleBio}>
                    <FormattedMessage
                      id={!showFullBio ? 'UserCard.seeMore' : 'UserCard.showLess'}
                    />
                  </InlineTextButton>
                </div>
              )}
            </div>
          ) : null}
          {links}
        </div>
      </div>
      {hasBio ? (
        <div className={css.mobileBio}>
          <div ref={mobileBioRef} className={viewBio} style={{ maxHeight: `${maxHeight}px` }}>
            <DraftContent draftContent={bio} />
          </div>
          {shouldShowSeeMore && (
            <div className={css.btnBox}>
              <InlineTextButton onClick={toggleBio}>
                <FormattedMessage id={!showFullBio ? 'UserCard.seeMore' : 'UserCard.showLess'} />
              </InlineTextButton>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

UserCard.defaultProps = {
  rootClassName: null,
  className: null,
  user: null,
  currentUser: null,
  showContact: true,
};

UserCard.propTypes = {
  rootClassName: string,
  className: string,
  user: oneOfType([propTypes.user, propTypes.currentUser]),
  currentUser: propTypes.currentUser,
  onContactUser: func.isRequired,
  showContact: bool,
};

export default UserCard;
